import React, { useRef, useState } from 'react';
import { IoEllipsisHorizontalOutline, IoDesktopSharp } from 'react-icons/io5';
import { GoDeviceMobile } from 'react-icons/go';
import { renderOptions } from '../form/selectOptions';
import Subheading from '../font/Subheading';
import styled from 'styled-components';

import Row from '../layout/Row';
import ColumnItem from '../layout/ColumnItem';
import Spacer from '../layout/Spacer';
import RadioButtonWrapper from '../radio/RadioButtonWrapper';
import { TwitterPicker } from 'react-color';
import {
  Padding,
  Margin,
  FlexType,
  Width,
  FontWeight,
  HoverStyle,
  ButtonPosition,
} from '../../types/fitQuizButton/ButtonState';
import { useAppSelector } from '../../redux/redux.hooks';
import { useClickOutside } from '../../hooks/use.click.outside';

type DeviceType = 'desktop' | 'mobile';

type FitquizAdvancedSettingsProps = {
  deviceType: DeviceType;
  handleFontWeightChanged: (value: FontWeight) => void;
  handleFontSizeChanged: (value: number) => void;
  handleUnderlineChanged: (value: string) => void;
  handleOnlyCapitalLettersChanged: (value: string) => void;
  handleJustifyContent: (flexType: FlexType) => void;
  handleChangeWidth: (width: Width) => void;
  handleChangePadding: (padding: Padding) => void;
  handleChangeMargin: (margin: Margin) => void;
  handleChangeBorderType: (value: any) => void;
  handleChangeBorderRadius: (value: number) => void;
  handleChangeBorderShape: (value: any) => void;
  handleChangeBorderColor: (value: string) => void;
  handleChangeBackgroundColor: (value: string) => void;
  handleChangeHoverStyle: (hoverStyle: HoverStyle) => void;
  handleChangeHoverBackgroundColor: (backgroundColor: string) => void;
  handleChangeButtonPosition: (buttonPosition: ButtonPosition) => void;
  resetData: () => void;
};

const FitquizAdvancedSettings = ({
  deviceType,
  handleFontWeightChanged,
  handleFontSizeChanged,
  handleUnderlineChanged,
  handleOnlyCapitalLettersChanged,
  handleJustifyContent,
  handleChangeWidth,
  handleChangePadding,
  handleChangeMargin,
  handleChangeBorderRadius,
  handleChangeBorderType,
  handleChangeBorderColor,
  handleChangeBorderShape,
  handleChangeBackgroundColor,
  handleChangeHoverStyle,
  handleChangeButtonPosition,
  handleChangeHoverBackgroundColor,
  resetData,
}: FitquizAdvancedSettingsProps) => {
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [showBorderColorPicker, setShowBorderColorPicker] =
    useState<boolean>(false);
  const [showBackgroundColorPicker, setShowBackgroundColorPicker] =
    useState<boolean>(false);
  const [showHoverBackgroundColorPicker, setShowHoverBackgroundColorPicker] =
    useState<boolean>(false);
  const borderColorRef = useRef<HTMLDivElement>(null);
  const backgroundColorRef = useRef<HTMLDivElement>(null);
  const hoverBackgroundColorRef = useRef<HTMLDivElement>(null);
  const { data } = useAppSelector((state) => state.fitButtonReducer);
  const isMobile = deviceType === 'mobile';
  const { desktopAdvancedSettings: desktop, mobileAdvancedSettings: mobile } =
    data;

  useClickOutside(borderColorRef, () => setShowBorderColorPicker(false));
  useClickOutside(backgroundColorRef, () =>
    setShowBackgroundColorPicker(false)
  );
  useClickOutside(hoverBackgroundColorRef, () =>
    setShowHoverBackgroundColorPicker(false)
  );

  const fontWeightOptions = [
    { label: 'Regular', value: '400' },
    { label: 'Semibold', value: '600' },
    { label: 'Bold', value: '700' },
  ];

  const fontSizeOptions = [
    { label: '10px', value: '10' },
    { label: '11px', value: '11' },
    { label: '12px', value: '12' },
    { label: '13px', value: '13' },
    { label: '14px', value: '14' },
    { label: '15px', value: '15' },
    { label: '16px', value: '16' },
    { label: '17px', value: '17' },
    { label: '18px', value: '18' },
    { label: '19px', value: '19' },
    { label: '20px', value: '20' },
    { label: '21px', value: '21' },
    { label: '22px', value: '22' },
    { label: '23px', value: '23' },
    { label: '24px', value: '24' },
    { label: '25px', value: '25' },
  ];

  const underlineOptions = [
    { label: 'Yes', value: 'true' },
    { label: 'No', value: 'false' },
  ];

  const onlyCapitalLettersOptions = [
    { label: 'Yes', value: 'true' },
    { label: 'No', value: 'false' },
  ];

  const borderOptions = [
    { label: 'None', value: 'none' },
    { label: 'Bottom', value: 'bottom' },
    { label: 'All', value: 'all' },
  ];

  const borderShapeOptions = [
    { label: 'Solid', value: 'solid' },
    { label: 'Dashed', value: 'dashed' },
    { label: 'Dotted', value: 'dotted' },
  ];

  const borderRadiusOptions = [
    { label: '0px', value: '0' },
    { label: '4px', value: '4' },
    { label: '6px', value: '6' },
    { label: '8px', value: '8' },
    { label: '10px', value: '10' },
    { label: '12px', value: '12' },
    { label: '14px', value: '14' },
    { label: '16px', value: '16' },
    { label: '18px', value: '18' },
    { label: '20px', value: '20' },
    { label: '22px', value: '22' },
    { label: '24px', value: '24' },
    { label: '26px', value: '26' },
  ];

  return (
    <div>
      <SectionHeader
        style={{ justifyContent: 'space-between' }}
        onClick={() => setShowSettings(!showSettings)}
      >
        <CustomHeading>
          <s-heading fontSize='large'>Advanced Settings</s-heading> &nbsp; &nbsp;
          {deviceType === 'desktop' ? (
            <IoDesktopSharp size={16} />
          ) : (
            <GoDeviceMobile size={16} />
          )}
        </CustomHeading>
        <IoEllipsisHorizontalOutline height={48} width={48} />
      </SectionHeader>
      {showSettings && (
        <Row>
          <ColumnItem>
            <s-select
              label='Font weight'
              onChange={(event) => handleFontWeightChanged(event.currentTarget.value as FontWeight)}
              value={isMobile ? mobile.fontWeight : desktop.fontWeight}
            >
              {renderOptions(fontWeightOptions)}
            </s-select>
            <Spacer height={12} />
            <s-select
              label='Font size'
              onChange={({ currentTarget: { value } }) => handleFontSizeChanged(+value)}
              value={isMobile ? mobile.fontSize.toString() : desktop.fontSize.toString()}
            >
              {renderOptions(fontSizeOptions)}
            </s-select>
            <Spacer height={12} />
            <s-select
              label='Underline'
              onChange={(event) => handleUnderlineChanged(event.currentTarget.value)}
              value={
                isMobile
                  ? mobile.underline.toString()
                  : desktop.underline.toString()
              }
            >
              {renderOptions(underlineOptions)}
            </s-select>
            <Spacer height={12} />
            <s-select
              label='Only capital letters'
              onChange={(event) => handleOnlyCapitalLettersChanged(event.currentTarget.value)}
              value={
                isMobile
                  ? mobile.onlyCapitalLetters.toString()
                  : desktop.onlyCapitalLetters.toString()
              }
            >
              {renderOptions(onlyCapitalLettersOptions)}
            </s-select>

            <Spacer height={24} />
            <Subheading>TEXT ALIGNMENT</Subheading>
            <Spacer height={8} />
            <Row>
              <RadioButtonWrapper>
                <s-button
                  variant={(isMobile ? mobile.justifyContent === 'flex-start' : desktop.justifyContent === 'flex-start') ? 'primary' : 'secondary'}
                  onClick={() => handleJustifyContent('flex-start')}
                  icon='text-align-left'
                  accessibilityLabel='Align left'
                />
              </RadioButtonWrapper>
              <RadioButtonWrapper>
                <s-button
                  variant={(isMobile ? mobile.justifyContent === 'center' : desktop.justifyContent === 'center') ? 'primary' : 'secondary'}
                  onClick={() => handleJustifyContent('center')}
                  icon='text-align-center'
                  accessibilityLabel='Align center'
                />
              </RadioButtonWrapper>
              <RadioButtonWrapper>
                <s-button
                  variant={(isMobile ? mobile.justifyContent === 'flex-end' : desktop.justifyContent === 'flex-end') ? 'primary' : 'secondary'}
                  onClick={() => handleJustifyContent('flex-end')}
                  icon='text-align-right'
                  accessibilityLabel='Align right'
                />
              </RadioButtonWrapper>
            </Row>

            <Spacer height={24} />
            <Subheading>BUTTON WIDTH</Subheading>
            <Spacer height={8} />
            <Row>
              <s-button
                variant={(isMobile ? mobile.width === 'auto' : desktop.width === 'auto') ? 'primary' : 'secondary'}
                onClick={() => handleChangeWidth('auto')}
              >
                Auto
              </s-button>
              <Spacer width={12} />
              <s-button
                variant={(isMobile ? mobile.width === 'fit-content' : desktop.width === 'fit-content') ? 'primary' : 'secondary'}
                onClick={() => handleChangeWidth('fit-content')}
              >
                Fit content
              </s-button>
            </Row>

            <Spacer height={24} />
            <Row>
              <s-number-field
                label='Vertical padding'
                onInput={({ currentTarget: { value } }) => {
                  const padding = isMobile ? mobile.padding : desktop.padding;
                  handleChangePadding({
                    ...padding,
                    vertical: +value,
                  });
                }}
                value={
                  isMobile
                    ? mobile.padding.vertical.toString()
                    : desktop.padding.vertical.toString()
                }
                autocomplete='off'
              />
              <Spacer width={12} />
              <s-number-field
                label='Horizontal padding'
                onInput={({ currentTarget: { value } }) => {
                  const padding = isMobile ? mobile.padding : desktop.padding;
                  handleChangePadding({
                    ...padding,
                    horizontal: +value,
                  });
                }}
                value={
                  isMobile
                    ? mobile.padding.horizontal.toString()
                    : desktop.padding.horizontal.toString()
                }
                autocomplete='off'
              />
            </Row>
            <Spacer height={12} />
            <Row>
              <s-number-field
                label='Top margin'
                onInput={({ currentTarget: { value } }) => {
                  const margin = isMobile ? mobile.margin : desktop.margin;
                  handleChangeMargin({
                    ...margin,
                    top: +value,
                  });
                }}
                value={
                  isMobile
                    ? mobile.margin.top.toString()
                    : desktop.margin.top.toString()
                }
                autocomplete='off'
              />
              <Spacer width={12} />
              <s-number-field
                label='Bottom margin'
                onInput={({ currentTarget: { value } }) => {
                  const margin = isMobile ? mobile.margin : desktop.margin;
                  handleChangeMargin({
                    ...margin,
                    bottom: +value,
                  });
                }}
                value={
                  isMobile
                    ? mobile.margin.bottom.toString()
                    : desktop.margin.bottom.toString()
                }
                autocomplete='off'
              />
              <Spacer width={12} />
              <s-number-field
                label='Horizontal'
                onInput={({ currentTarget: { value } }) => {
                  const margin = isMobile ? mobile.margin : desktop.margin;
                  handleChangeMargin({
                    ...margin,
                    horizontal: +value,
                  });
                }}
                value={
                  isMobile
                    ? mobile.margin.horizontal.toString()
                    : desktop.margin.horizontal.toString()
                }
                autocomplete='off'
              />
            </Row>
          </ColumnItem>
          <ColumnItem>
            <s-select
              label='Borders'
              onChange={({ currentTarget: { value } }) => handleChangeBorderType(value)}
              value={isMobile ? mobile.borderType : desktop.borderType}
            >
              {renderOptions(borderOptions)}
            </s-select>
            <Spacer height={12} />
            <s-select
              disabled={
                isMobile
                  ? mobile.borderType === 'none'
                  : desktop.borderType === 'none'
              }
              label='Border shape'
              onChange={({ currentTarget: { value } }) => handleChangeBorderShape(value)}
              value={isMobile ? mobile.borderShape : desktop.borderShape}
            >
              {renderOptions(borderShapeOptions)}
            </s-select>
            <Spacer height={12} />
            <s-select
              label='Border radius'
              onChange={({ currentTarget: { value } }) => handleChangeBorderRadius(+value)}
              value={
                isMobile
                  ? mobile.borderRadius.toString()
                  : desktop.borderRadius.toString()
              }
            >
              {renderOptions(borderRadiusOptions)}
            </s-select>
            <Spacer height={12} />
            <s-button
              disabled={
                isMobile
                  ? mobile.borderType === 'none'
                  : desktop.borderType === 'none'
              }
              onClick={() => setShowBorderColorPicker(!showBorderColorPicker)}
            >
              Border color
            </s-button>
            {showBorderColorPicker && (
              <>
                <Spacer height={12} />
                <Row ref={borderColorRef}>
                  <TwitterPicker
                    color={isMobile ? mobile.borderColor : desktop.borderColor}
                    onChangeComplete={(value) =>
                      handleChangeBorderColor(value.hex)
                    }
                  />
                </Row>
              </>
            )}
            <Spacer height={12} />
            <s-button
              onClick={() =>
                setShowBackgroundColorPicker(!showBackgroundColorPicker)
              }
            >
              Background color
            </s-button>
            {showBackgroundColorPicker && (
              <>
                <Spacer height={12} />
                <Row ref={backgroundColorRef}>
                  <TwitterPicker
                    color={
                      isMobile
                        ? mobile.backgroundColor
                        : desktop.backgroundColor
                    }
                    onChangeComplete={(value) =>
                      handleChangeBackgroundColor(value.hex)
                    }
                  />
                </Row>
              </>
            )}
            <Spacer height={24} />

            <Subheading>BUTTON PLACEMENT</Subheading>
            <Spacer height={8}/>
            <Row>
            <RadioButtonWrapper style={{ width: 'fit-content' }}>
                <s-button
                  variant={(isMobile ? mobile.buttonPosition === 'Default' : desktop.buttonPosition === 'Default') ? 'primary' : 'secondary'}
                  onClick={() => handleChangeButtonPosition('Default')}
                >
                  Default
                </s-button>
              </RadioButtonWrapper>
              <RadioButtonWrapper style={{ width: 'fit-content' }}>
                <s-button
                  variant={(isMobile ? mobile.buttonPosition === 'Right' : desktop.buttonPosition === 'Right') ? 'primary' : 'secondary'}
                  onClick={() => handleChangeButtonPosition('Right')}
                >
                  Right
                </s-button>
              </RadioButtonWrapper>
              <RadioButtonWrapper style={{ width: 'fit-content' }}>
                <s-button
                  variant={(isMobile ? mobile.buttonPosition === 'Bottom' : desktop.buttonPosition === 'Bottom') ? 'primary' : 'secondary'}
                  onClick={() => handleChangeButtonPosition('Bottom')}
                >
                  Bottom
                </s-button>
              </RadioButtonWrapper>
              <RadioButtonWrapper style={{ width: 'fit-content' }}>
                <s-button
                  variant={(isMobile ? mobile.buttonPosition === 'Left' : desktop.buttonPosition === 'Left') ? 'primary' : 'secondary'}
                  onClick={() => handleChangeButtonPosition('Left')}
                >
                  Left
                </s-button>
              </RadioButtonWrapper>
            </Row>

            <Spacer height={24} />

            <Subheading>HOVER STYLE</Subheading>
            <Spacer height={8} />
            <Row>
              <RadioButtonWrapper style={{ width: 'fit-content' }}>
                <s-button
                  variant={(isMobile ? mobile.hoverStyle === 'None' : desktop.hoverStyle === 'None') ? 'primary' : 'secondary'}
                  onClick={() => handleChangeHoverStyle('None')}
                >
                  None
                </s-button>
              </RadioButtonWrapper>
              <Spacer width={8} />
              <RadioButtonWrapper>
                <s-button
                  variant={(isMobile ? mobile.hoverStyle === 'Underline' : desktop.hoverStyle === 'Underline') ? 'primary' : 'secondary'}
                  onClick={() => handleChangeHoverStyle('Underline')}
                >
                  Underline
                </s-button>
              </RadioButtonWrapper>
              <Spacer width={8} />
              <RadioButtonWrapper
                ref={hoverBackgroundColorRef}
                style={{ width: 'fit-content' }}
              >
                <s-button
                  variant={(isMobile ? mobile.hoverStyle === 'Background' : desktop.hoverStyle === 'Background') ? 'primary' : 'secondary'}
                  onClick={() => {
                    // handleChangeHoverStyle('Background');
                    setShowHoverBackgroundColorPicker((prev) => !prev);
                  }}
                >
                  Background
                </s-button>
                <Spacer height={24} />
                {showHoverBackgroundColorPicker && (
                  <>
                    <TwitterPicker
                      color={
                        isMobile
                          ? mobile.hoverBackgroundColor
                          : desktop.hoverBackgroundColor
                      }
                      onChangeComplete={(value) => {
                        handleChangeHoverStyle('Background');
                        handleChangeHoverBackgroundColor(value.hex);
                      }}
                    />
                  </>
                )}
              </RadioButtonWrapper>
            </Row>
            <Spacer height={72} />
            <ResetButtonContainer>
              <s-button variant='tertiary' onClick={resetData}>
                Reset all settings to default
              </s-button>
            </ResetButtonContainer>
          </ColumnItem>
        </Row>
      )}
    </div>
  );
};

export default FitquizAdvancedSettings;

const ResetButtonContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: flex-end;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px;
`;

const CustomHeading = styled.div`
  display: flex;
  align-items: center;
`;
